/**
 * AI Chat shared types and constants
 */

import type { QuickPrompt, LatestSession } from '../../../features/chat/api';
import type { AeternaLoopContextValue } from '../../../components/aeterna-loop';

export interface DisplayQuestion {
  label: string;
  value: string;
}

export const FALLBACK_QUESTIONS = [
  '我的睡眠怎么样？',
  '如何改善代谢？',
  '今日饮食建议',
  '解读体检报告',
];

export const REUSE_WINDOW_HOURS = 24;

export function isSessionReusable(session: LatestSession | null): boolean {
  if (!session) return false;
  if (session.status !== 'active') return false;
  if (!session.lastMessageAt) return false;
  const hours = (Date.now() - new Date(session.lastMessageAt).getTime()) / (1000 * 60 * 60);
  return hours <= REUSE_WINDOW_HOURS;
}

export function formatTimeAgo(dateStr: string | null): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${minutes}分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  return `${days}天前`;
}

export function buildDisplayQuestions(
  sessionQuickPrompts: string[] | null | undefined,
  quickPrompts: QuickPrompt[],
): DisplayQuestion[] {
  if (sessionQuickPrompts && sessionQuickPrompts.length > 0) {
    return sessionQuickPrompts.map(q => ({ label: q, value: q }));
  }
  if (quickPrompts.length > 0) {
    return quickPrompts.map(p => ({ label: p.title || p.promptText, value: p.promptText }));
  }
  return FALLBACK_QUESTIONS.map(q => ({ label: q, value: q }));
}

export function buildContextDisplayQuestions(
  context: AeternaLoopContextValue | null,
  baseQuestions: DisplayQuestion[],
): DisplayQuestion[] {
  if (!context?.askPrompt) return baseQuestions;
  const label =
    context.stage === 'act' && context.sourceType === 'practice' && context.sourceTitle.includes('本周计划')
      ? '问本周为什么这样排'
      : context.stage === 'act'
        ? '问今天为什么这样练'
        : '问这一条依据';
  return [{ label, value: context.askPrompt }, ...baseQuestions].slice(0, 4);
}

export const preprocessMarkdown = (text: string): string =>
  text
    .replace(/^\[\d{2}:\d{2}\]\s*/, '')
    .replace(/^\[\d{2}-\d{2}\s+\d{2}:\d{2}\]\s*/, '')
    .replace(/<br\s*\/?>/gi, '  \n')
    .replace(/\*\*\s+([^*]+?)\s+\*\*/g, '**$1**')
    .replace(/\*\s+([^*]+?)\s+\*/g, '*$1*');

/** Pipeline step label map — must stay in sync with backend feishu_cards::step_label */
export const PIPELINE_STEP_LABELS: Record<string, string> = {
  intent_router: '意图识别',
  attachment: '处理附件',
  context_setup: '加载健康数据',
  prompt_budget: '组装分析方案',
  tool_loop: '工具调用',
  streaming: '生成回复',
};

/** Canonical pipeline step order */
export const PIPELINE_STEPS = [
  'intent_router',
  'attachment',
  'context_setup',
  'prompt_budget',
  'tool_loop',
  'streaming',
] as const;

/**
 * Compute which steps to show and their states based on current phase/step.
 * Returns { label, state } where state is 'done' | 'active' | 'pending'.
 */
export function computeStepStates(
  phase?: string,
  step?: string,
): { label: string; state: 'done' | 'active' | 'pending' }[] {
  if (!phase || !step) return [];

  const phaseCutoff = phase === 'pipeline' ? 4
    : phase === 'tool_loop' ? 5
    : phase === 'streaming' ? 6
    : 4;

  const currentIdx = (PIPELINE_STEPS as readonly string[]).indexOf(step ?? '');
  const fallbackIdx = phase === 'tool_loop' ? 4 : phase === 'streaming' ? 5 : -1;
  const ci = currentIdx >= 0 ? currentIdx : fallbackIdx;
  if (ci < 0) return [];

  return PIPELINE_STEPS.slice(0, phaseCutoff).map((s, i) => ({
    label: PIPELINE_STEP_LABELS[s] || s,
    state: i < ci ? 'done' : i === ci ? 'active' : 'pending',
  }));
}
